import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Button, Select, Input, Modal, alert } from 'equasion-lib';
import { Form, Formik } from 'formik';
import { object, string, number } from 'yup';
import { FadeLoader } from 'react-spinners';
import { FiChevronLeft, FiMinusCircle, FiPlusCircle } from 'react-icons/fi';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import ZoneScanButton from '@/lib/shared-coms/ZoneScanner/ZoneScanButton';
import useZoneScanLookup from '@/lib/shared-coms/ZoneScanner/useZoneScanLookup';
import { SCAN_PROFILE } from '@/root/App/com/ScannerProvider/ScannerProvider';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import {
  zoneSdSuffix,
  zoneQuarantineSuffix,
  zonePickBucket
} from '@/lib/utils/zoneUtils';
import { playSuccessTone } from '@/lib/scanning/feedback';
import { scanAlert } from '@/lib/scanning/scanAlert';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import { isShortDatedExpiration } from '@/lib/utils/shortDated';
import './SalesOrderUnserializedPickScan.scss';

const BUCKET_LABELS = {
  stocked: 'Stocked',
  quarantined: 'Quarantined',
  destroyed: 'Destroyed'
};

function SalesOrderUnserializedPickScan({
  salesOrder,
  companyId,
  fdaProductId,
  upcProductId,
  refetch,
  zonesWithProduct,
  getClientProductZoneInventoryBalances,
  zoneInventoryBalances = [],
  zoneInventoryBalancesLoading = false,
  pickUnserializedSalesOrder,
  stockUnserializedSalesOrder,
  unserializedItemStates = [],
  isShortDatedParam = false,
  suggestedZoneId = null
}) {
  const navigate = useNavigate();
  const [scanLot, setScanLot] = useState(null);
  const [mode, setMode] = useState('add');
  const isAdd = mode === 'add';
  const [updateSubmitted, setUpdateSubmitted] = useState(false);
  const [zonesViewMoreOpen, setZonesViewMoreOpen] = useState(false);
  const [zonesPreviewOverflow, setZonesPreviewOverflow] = useState(false);
  const zonesPreviewRef = useRef(null);
  const {
    id: salesOrderId,
    vendorSalesOrderNumber,
    orderItems,
    isDestructionOrder
  } = salesOrder || {};
  // Return SOs may pick out of a quarantine zone, so the bucket depends on which
  // zone the picker selected rather than on the order alone.
  const zoneById = new Map((zonesWithProduct || []).map((zone) => [zone.id, zone]));
  const bucketForZone = (zoneId) =>
    zonePickBucket(zoneById.get(zoneId), { isDestructionOrder });
  const unserializedSalesOrderItem =
    orderItems.find(
      ({ product, isShortDated }) =>
        ((!!fdaProductId && product?.fdaProduct?.id === fdaProductId) ||
          (!!upcProductId && product?.upcProduct?.id === upcProductId)) &&
        !!isShortDated === !!isShortDatedParam
    ) ||
    // Fallback: match without isShortDated filter (for orders without duplicates)
    orderItems.find(
      ({ product }) =>
        (!!fdaProductId && product?.fdaProduct?.id === fdaProductId) ||
        (!!upcProductId && product?.upcProduct?.id === upcProductId)
    ) ||
    {};
  const {
    quantity: unserializedQuantity,
    unserializedItemOfStatusCount,
    isShortDated: orderItemIsShortDated,
    isChargeback: orderItemIsChargeback,
    product: lineProduct = {}
  } = unserializedSalesOrderItem || {};
  const { id: productId } = lineProduct;

  // Zone captured by pointing the always-on camera at a rack label, before the pick form is
  // even open. It seeds the form's zone so the picker's first action at the shelf — scanning
  // the zone they walked to — is also the one that puts that zone's lots in front of them.
  // Seeded with the stop the picking screen's walk sent the picker to, when that zone still
  // holds the product: the form then opens on that zone's lots straight away, exactly as if
  // the picker had scanned its label on arrival.
  const suggestedZone = (zonesWithProduct || []).find(({ id }) => id === suggestedZoneId);
  const [scannedZoneId, setScannedZoneId] = useState(
    suggestedZone ? suggestedZone.id : null
  );
  const [openEntryToken, setOpenEntryToken] = useState(suggestedZone ? 1 : 0);
  const [entryOpen, setEntryOpen] = useState(false);
  const lookupZoneCode = useZoneScanLookup({ companyId });

  const loadZoneBalances = (zoneId) =>
    getClientProductZoneInventoryBalances({
      variables: {
        companyId,
        productId,
        zoneId,
        balanceBucket: bucketForZone(zoneId),
        salesOrderId
      }
    }).catch((e) => console.error('zone balances load failed:', e));

  useEffect(() => {
    if (suggestedZone && isAdd) loadZoneBalances(suggestedZone.id);
    // On arrival only: the suggested zone comes from the URL and does not change afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZoneLabelScan = async (result) => {
    // Remove mode restocks from the picked-item list, where a zone is derived rather than
    // chosen — a rack label means nothing there.
    if (!isAdd) return;
    // The camera keeps running behind the pick form, so a rack still in frame while the
    // operator types a quantity would otherwise re-seed the zone and reset the form under
    // them. Changing zone mid-pick is done from inside the form, not by the page camera.
    if (entryOpen) return;
    const code = (result?.text || '').trim();
    if (!code) return;
    // A GTIN or an SSCC means the operator scanned the box, not the rack; saying so beats "no
    // zone matches". Keyed on those two rather than on "is it GS1" because a zone label can
    // itself be a GS1 element string — a GLN or an internal AI — and must still be looked up.
    if (result?.gtin || result?.sscc) {
      scanAlert(
        'That is a product barcode',
        'Scan the zone label on the rack instead — the pick quantity is entered by hand.',
        5,
        'warning'
      );
      return;
    }

    const { zone, matches, error } = await lookupZoneCode(code);
    if (error) {
      scanAlert(
        'Zone lookup failed',
        operatorFacingMessage(error, 'Could not look up that zone. Please try again.'),
        6,
        'error'
      );
      return;
    }
    if (!zone) {
      scanAlert(
        matches.length > 1 ? 'More than one zone matches' : 'Zone not found',
        matches.length > 1
          ? `“${code}” matches ${matches.length} zones. Choose the zone from the list instead.`
          : `No zone matches “${code}”. Check the label or choose the zone from the list.`,
        6,
        'error'
      );
      return;
    }
    if (!(zonesWithProduct || []).some(({ id }) => id === zone.id)) {
      scanAlert(
        'Nothing to pick there',
        `${zone.name} holds no stock of this product for this order.`,
        6,
        'warning'
      );
      return;
    }

    playSuccessTone();
    setScannedZoneId(zone.id);
    loadZoneBalances(zone.id);
    setOpenEntryToken((token) => token + 1);
  };

  const executeUnserializedPick = async (data, actions) => {
    const { setSubmitting } = actions;
    const parsedQuantity = Number(data.quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setSubmitting(false);
      return;
    }
    try {
      await pickUnserializedSalesOrder({
        variables: {
          salesOrderId,
          zoneId: data.zoneId,
          zoneInventoryBalanceId: data.zoneInventoryBalanceId,
          quantity: parsedQuantity,
          companyId: companyId
        }
      });
      if (isAdd) {
        setUpdateSubmitted(true);
      }
      await refetch();
    } catch (e) {
      const errorMessage = e?.message || '';
      // Chargeback is tested FIRST: a line can be both short-dated and chargeback, and the
      // lot-level provenance check is the one that fires here (this screen's balances carry no
      // supplier of their own — see assertChargebackUnserializedPick). `chargeback` rather than
      // `chargeback-eligible` because the vendor-named wording of the refusal drops the longer
      // phrase; lowercase keeps it clear of the "Chargeback agreement ..." CRUD errors.
      const alertTitle = errorMessage.includes('chargeback')
        ? 'Chargeback stock required'
        : errorMessage.includes('requires short-dated inventory')
        ? 'Short-dated lot required'
        : 'Pick failed';
      alert(
        alertTitle,
        operatorFacingMessage(e, 'Unable to pick this lot. Please try again.'),
        6,
        'error'
      );
    }
    setSubmitting(false);
  };

  const submitUnserializedPick = async (data, actions) => {
    if (orderItemIsShortDated) {
      const selectedBalance = zoneInventoryBalances?.find(
        ({ id }) => id === data.zoneInventoryBalanceId
      );
      if (!isShortDatedExpiration(selectedBalance?.expiration)) {
        actions.setSubmitting(false);
        alert(
          'Short-dated lot required',
          'This order item requires short-dated inventory (expires within 180 days). The selected lot does not qualify.',
          6,
          'error'
        );
        return;
      }
    }
    await executeUnserializedPick(data, actions);
  };

  const submitUnserializedStock = async (data, actions) => {
    const { setSubmitting } = actions;
    const parsedQuantity = Number(data.quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setSubmitting(false);
      return;
    }
    try {
      await stockUnserializedSalesOrder({
        variables: {
          salesOrderId,
          zoneId: data.zoneId,
          unserializedItemStateId: data.unserializedItemStateId,
          quantity: parsedQuantity,
          companyId: companyId
        }
      });
      await refetch();
      // setModalOpen(false);
    } catch (e) {
      alert(
        'Unpick failed',
        operatorFacingMessage(e, 'Unable to return this lot. Please try again.'),
        6,
        'error'
      );
    }
    setSubmitting(false);
  };

  const zonesPreviewText = zonesWithProduct
    .map((zone) => zone.name + zoneQuarantineSuffix(zone) + zoneSdSuffix(zone))
    .join(', ');
  const zonesPreviewKey = zonesWithProduct.map(({ id }) => id).join(',');
  const handleZonesPreviewRef = (el) => {
    zonesPreviewRef.current = el;
    if (!el) return;
    const overflowing = el.scrollHeight > el.clientHeight + 1;
    setZonesPreviewOverflow((prev) => (prev === overflowing ? prev : overflowing));
  };

  useEffect(() => {
    if (
      isAdd &&
      updateSubmitted &&
      unserializedQuantity === unserializedItemOfStatusCount
    ) {
      navigate(`/sales-order-picking/${salesOrderId}`);
    }
  }, [
    isAdd,
    navigate,
    salesOrderId,
    unserializedItemOfStatusCount,
    unserializedQuantity,
    updateSubmitted
  ]);

  return (
    <Page className="sales-order-unserialized-pick-scan action-page">
      <div className="action-top-bar">
        <div className="top">
          <Button
            type="secondary"
            iconBefore={<FiChevronLeft />}
            onClick={() => navigate(`/sales-order-picking/${salesOrderId}`)}
          >
            Back
          </Button>
          {/* The SO number is the on-floor identity of what is being picked. It was always in this
              query and never rendered; with totes optional it is the substitute for the tote label
              a picker used to have in hand. Unconditional — useful either way. */}
          <div>
            Picking{vendorSalesOrderNumber ? ` — SO #${vendorSalesOrderNumber}` : ''}
          </div>
        </div>

        <div className="bottom">
          <div className="default-location">
            {zonesWithProduct.length === 1 ? 'Zone:' : 'Zones:'}
            <div
              key={zonesPreviewKey}
              ref={handleZonesPreviewRef}
              className="zones-preview"
            >
              {zonesPreviewText}
            </div>
            {zonesPreviewOverflow && (
              <Button
                type="secondary"
                className="zones-view-more-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setZonesViewMoreOpen(true);
                }}
              >
                View All
              </Button>
            )}
          </div>
          <div className="sales-order-unserialized-pick-scan__side-stack">
            <div className="sales-order-unserialized-pick-scan__line-panel sales-order-unserialized-pick-scan__line-panel--count">
              {unserializedItemOfStatusCount} of {unserializedQuantity}
            </div>
            <div className="sales-order-unserialized-pick-scan__line-panel sales-order-unserialized-pick-scan__line-panel--product">
              <WarehouseProductDisplay
                product={lineProduct}
                compact
                className="sales-order-unserialized-pick-scan__product-display"
              >
                {orderItemIsShortDated && (
                  <span
                    style={{ color: 'var(--orange, orange)', fontWeight: 'bold' }}
                  >
                    SHORT DATED
                  </span>
                )}
                {/* Purple, distinct from the orange short-dated warning: the restriction is on
                    where the units were BOUGHT, not on their expiration. It matters most on this
                    screen, where the Lot/Exp list is silently pre-filtered to eligible lots and
                    a missing lot would otherwise look like missing stock. */}
                {orderItemIsChargeback && (
                  <span
                    style={{ color: 'var(--purple, purple)', fontWeight: 'bold' }}
                  >
                    CHARGEBACK
                  </span>
                )}
              </WarehouseProductDisplay>
            </div>
          </div>
        </div>
      </div>
      <div className="action-page-body">
        <div
          className={['mode', mode].join(' ')}
          onClick={() => setMode(isAdd ? 'remove' : 'add')}
        >
          {mode} {isAdd ? <FiPlusCircle /> : <FiMinusCircle />}
        </div>
        {isAdd && (
          <div className="zone-scan-hint">
            Scan a zone label to pick from that zone
          </div>
        )}
        <SharedScanner
          scanLot={scanLot}
          setScanLot={setScanLot}
          openEntryToken={openEntryToken}
          onEntryOpenChange={setEntryOpen}
          // This screen's camera reads zone rack labels, never item barcodes — the pick
          // quantity is entered by hand.
          scanProfile={SCAN_PROFILE.ZONE}
          handleScan={handleZoneLabelScan}
          replaceManualEntry={true}
          alternativeManualEntry={
            isAdd ? (
              <Formik
                initialValues={{
                  // Seeded by a zone scan taken on the picking screen itself; `enableReinitialize`
                  // below is what carries a freshly scanned zone into the open form.
                  zoneId: scannedZoneId,
                  zoneInventoryBalanceId: null,
                  quantity: 0
                }}
                validationSchema={object().shape({
                  zoneId: string().required('Zone is required'),
                  zoneInventoryBalanceId: string().required('Lot/Exp is required'),
                  quantity: number()
                    .min(1, 'Quantity must be greater than 0')
                    .required('Quantity is required')
                })}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={submitUnserializedPick}
                enableReinitialize
              >
                {({ isSubmitting, setFieldValue, values, errors, submitCount }) => {
                  const { zoneId, zoneInventoryBalanceId, quantity } = values;
                  const balanceBucket = bucketForZone(zoneId);
                  // Selecting a zone — by tapping the list or by scanning the rack label —
                  // resets the lot choice and reloads that zone's lots, so both paths land
                  // the picker in the same state.
                  const selectZone = (nextZoneId) => {
                    setFieldValue('zoneId', nextZoneId);
                    setFieldValue('zoneInventoryBalanceId', null);
                    loadZoneBalances(nextZoneId);
                  };
                  const quantityMustBePositive =
                    submitCount > 0 && quantity !== '' && Number(quantity) <= 0;
                  const pickedMoreThanAvailable =
                    quantity >
                      zoneInventoryBalances.find(
                        ({ id }) => id === zoneInventoryBalanceId
                      )?.[balanceBucket] || 0;
                  const quantityMoreThanNeeded =
                    quantity + unserializedItemOfStatusCount > unserializedQuantity;
                  return (
                    <Form>
                      <div className="otc-pick-row">
                        <div>
                          <div className="quantity">{unserializedQuantity}</div>
                          <div className="name">
                            <WarehouseProductDisplay product={lineProduct} compact />
                          </div>
                        </div>
                      </div>
                      <p>
                        Please gather the quantity listed above and manually confirm the
                        pick.
                      </p>
                      <div className="zone-select-with-scan">
                        <div className="zone-select-with-scan__field">
                          <Select
                            form={false}
                            label="Zone"
                            name="zoneId"
                            options={zonesWithProduct.map((zone) => [
                              zone.id,
                              zone.name + zoneQuarantineSuffix(zone) + zoneSdSuffix(zone)
                            ])}
                            onChange={({ value }) => selectZone(value)}
                            value={zoneId}
                          />
                        </div>
                        <ZoneScanButton
                          companyId={companyId}
                          title="Scan Pick Zone"
                          hint="Scan the label on the zone you are picking this product from."
                          // Resolution runs against every zone in the company, then this
                          // narrows it to the zones that actually hold the line's product —
                          // so scanning the wrong rack says why instead of "not found".
                          allowedZones={zonesWithProduct}
                          allowedZonesMessage="That zone holds no stock of this product for this order."
                          manualEntryLabel="Choose zone from the list"
                          onSelect={(zone) => selectZone(zone.id)}
                        />
                      </div>
                      {!!zoneId && !zoneInventoryBalancesLoading && (
                        <Select
                          form={false}
                          label="Lot/Exp"
                          name="zoneInventoryBalanceId"
                          options={zoneInventoryBalances
                            .filter((b) => b.zone?.id === zoneId)
                            .map((balance) => [
                              balance.id,
                              `${balance.lot || 'no lot'} • ${
                                formatDateOnly(balance.expiration) || 'no exp'
                              } • ${BUCKET_LABELS[balanceBucket]}:${
                                balance[balanceBucket] || 0
                              }`
                            ])}
                          onChange={({ value }) =>
                            setFieldValue('zoneInventoryBalanceId', value)
                          }
                          value={zoneInventoryBalanceId}
                        />
                      )}
                      {!!zoneInventoryBalanceId && (
                        <Input
                          label="Quantity"
                          name="quantity"
                          inputType="number"
                          min={1}
                          step={1}
                        />
                      )}
                      {[
                        ...Object.values(errors),
                        quantityMustBePositive && 'Quantity must be greater than 0',
                        pickedMoreThanAvailable && 'Quantity is greater than available',
                        quantityMoreThanNeeded && 'Quantity is greater than needed'
                      ]
                        .filter(Boolean)
                        .map((error) => (
                          <div key={error} className="error-message">
                            {error}
                          </div>
                        ))}
                      <Button
                        type="primary"
                        fullWidth
                        isSubmit
                        disabled={
                          isSubmitting ||
                          !zoneId ||
                          !zoneInventoryBalanceId ||
                          !quantity ||
                          quantityMustBePositive ||
                          pickedMoreThanAvailable ||
                          quantityMoreThanNeeded
                        }
                      >
                        Confirm Unserialized Pick
                      </Button>

                      {!!isSubmitting && (
                        <div
                          className="blocking-spinner-container"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FadeLoader color="var(--n0)" />
                        </div>
                      )}
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Formik
                initialValues={{
                  zoneId: null,
                  unserializedItemStateId: null,
                  quantity: 0
                }}
                validationSchema={object().shape({
                  zoneId: string().required('Zone is required'),
                  unserializedItemStateId: string().required('Picked item is required'),
                  quantity: number()
                    .min(1, 'Quantity must be greater than 0')
                    .required('Quantity is required')
                })}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={submitUnserializedStock}
                enableReinitialize
              >
                {({ isSubmitting, setFieldValue, values, errors, submitCount }) => {
                  const { zoneId, unserializedItemStateId, quantity } = values;
                  const quantityMustBePositive =
                    submitCount > 0 && quantity !== '' && Number(quantity) <= 0;
                  const selectedState = unserializedItemStates.find(
                    ({ id }) => id === unserializedItemStateId
                  );
                  const pickedMoreThanAvailable =
                    quantity > (selectedState?.quantity || 0);
                  return (
                    <Form>
                      <div className="otc-pick-row">
                        <div>
                          <div className="quantity">{unserializedItemOfStatusCount}</div>
                          <div className="name">
                            <WarehouseProductDisplay product={lineProduct} compact />
                          </div>
                        </div>
                      </div>
                      <p>Please restock the unserialized item.</p>
                      {unserializedItemStates.length === 0 ? (
                        <p>No items picked for this product.</p>
                      ) : (
                        <Select
                          form={false}
                          label="Picked Item"
                          name="unserializedItemStateId"
                          options={unserializedItemStates.map(
                            ({ id, lot, expiration, quantity, zone }) => [
                              id,
                              `${zone?.name || 'Unknown Zone'} • ${lot || 'no lot'} • ${
                                formatDateOnly(expiration) || 'no exp'
                              } • Qty:${quantity}`
                            ]
                          )}
                          onChange={({ value }) => {
                            setFieldValue('unserializedItemStateId', value);
                            const item = unserializedItemStates.find(
                              ({ id }) => id === value
                            );
                            setFieldValue('zoneId', item?.zoneId || item?.zone?.id);
                          }}
                          value={unserializedItemStateId}
                        />
                      )}
                      {!!unserializedItemStateId && selectedState?.zone && (
                        <div style={{ padding: '0.5rem 0', fontSize: '1.4rem' }}>
                          Place back in zone: <strong>{selectedState.zone.name}</strong>
                        </div>
                      )}
                      {!!unserializedItemStateId && (
                        <Input
                          label="Quantity"
                          name="quantity"
                          inputType="number"
                          min={1}
                          step={1}
                        />
                      )}
                      {[
                        ...Object.values(errors),
                        quantityMustBePositive && 'Quantity must be greater than 0',
                        pickedMoreThanAvailable && 'Quantity is greater than available'
                      ]
                        .filter(Boolean)
                        .map((error) => (
                          <div key={error} className="error-message">
                            {error}
                          </div>
                        ))}
                      <Button
                        type="primary"
                        fullWidth
                        isSubmit
                        disabled={
                          isSubmitting ||
                          !zoneId ||
                          !unserializedItemStateId ||
                          !quantity ||
                          quantityMustBePositive ||
                          pickedMoreThanAvailable
                        }
                      >
                        Confirm Unserialized Restock
                      </Button>

                      {!!isSubmitting && (
                        <div
                          className="blocking-spinner-container"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FadeLoader color="var(--n0)" />
                        </div>
                      )}
                    </Form>
                  );
                }}
              </Formik>
            )
          }
        />
      </div>
      <Modal
        className="zones-view-more-modal"
        modalOpen={zonesViewMoreOpen}
        setModalOpen={setZonesViewMoreOpen}
      >
        <div className="zones-view-more-list">
          <strong>Zones:</strong>
          {zonesWithProduct.map(({ id, name, shortDatedQuantity }) => (
            <div className="zones-view-more-row" key={id}>
              <span>{name}</span>
              {shortDatedQuantity > 0 && (
                <span className="zones-view-more-row__sd-badge">
                  SD {shortDatedQuantity}
                </span>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </Page>
  );
}

export default SalesOrderUnserializedPickScan;
